import { estimateParagraphCost, estimateWrapperCost } from './estimate-cost';
import type { EstimateTokens } from './estimate-cost';
import { expandHeadings } from './expandHeadings';
import { expandNeighbors } from './expandNeighbors';
import { expandParentListItems } from './expandParentListItems';
import { expandTableRows, findContainingTable, getRowOfEntry } from './expandTableRows';
import { indexParagraphs } from './indexParagraphs';
import type { ParagraphAncestor, ParagraphEntry } from './indexParagraphs';
import { mutateAllIncluded } from '../helpers/mutateAllIncluded';
import type { SerializeOptions } from '../serialize/serializeOptions';
import type { CastRoot, CastTable, CastTableRow } from '../types';

export type ExpandCastOptions = {
  /**
   * Token estimator. Should use the same tokenizer as the consumer (OpenAI,
   * Anthropic, etc.). Pass `text => text.length` for tests.
   */
  estimateTokens: EstimateTokens;
  /**
   * Number of neighbor paragraphs to include on each side of a non-table
   * match (scoped to the same section). Set to 0 to disable.
   */
  expandNeighborCount: number;
  /** Maximum cumulative token budget for the included subset. Use Infinity for no cap. */
  maxTokenBudget: number;
  /**
   * Maximum tokens for table *context* rows — the header and the rows
   * surrounding a match. Matched rows are always included and don't count
   * against this. Use Infinity to include every row the global
   * `maxTokenBudget` allows.
   */
  perTableTokenLimit: number;
};

/**
 * Mutates `root` so only the matched paragraphs (and their expanded context)
 * have `included: true`, capped by the given token budget. Resets every
 * paragraph/section/table/row/cell to `included: false` first, then walks the
 * matchedIds in priority order until the budget is exhausted.
 *
 * Expansion per match includes: the match itself, ±N neighbor paragraphs in
 * the same section/cell, parent list items (for nested lists), and preceding
 * scoping headings. When the match lives in a table, the table is expanded
 * row by row instead — matched rows always, the header row if it fits, and
 * surrounding rows alternating outward within `tableTokenLimit` — so a large
 * table contributes only the relevant rows rather than the whole thing.
 *
 * Wrapper costs (the <header>/<main>/<footer>, <table>, <tr>, <td> tags)
 * are charged once per wrapper, the first time something inside it is added
 * to the included set.
 *
 * Returns the same root (mutation is in place; the return is for chaining).
 */
export function expandCast(
  root: CastRoot,
  matchedIds: string[],
  expandOptions: ExpandCastOptions,
  serializeOptions: SerializeOptions
): CastRoot {
  mutateAllIncluded(root, false);

  const index = indexParagraphs(root);
  const {
    estimateTokens,
    expandNeighborCount: neighborCount,
    maxTokenBudget: maxBudget,
    perTableTokenLimit: tableTokenLimit,
  } = expandOptions;

  let spent = 0;
  const includedAncestors = new Set<ParagraphAncestor>();
  const processedTables = new Set<CastTable>();

  /** Marks a paragraph and all its ancestor wrappers as included. */
  const includeEntry = (entry: ParagraphEntry): void => {
    entry.paragraph.included = true;
    for (const ancestor of entry.ancestors) {
      if (ancestor.included !== true) {
        ancestor.included = true;
        includedAncestors.add(ancestor);
      }
    }
  };

  /**
   * Commits a set of paragraph entries as one unit if they fit the remaining
   * budget (or if nothing has been included yet, so the result is non-empty).
   * Returns false when the budget rejects them.
   */
  const tryCommit = (entries: ParagraphEntry[]): boolean => {
    const additions = entries.filter(entry => entry.paragraph.included !== true);
    if (additions.length === 0) return true;

    const cost = estimateAdditionCost(
      additions,
      includedAncestors,
      serializeOptions,
      estimateTokens
    );
    if (spent !== 0 && spent + cost > maxBudget) return false;

    for (const entry of additions) includeEntry(entry);
    spent += cost;
    return true;
  };

  /**
   * Includes an entire row — the row, all its cells (including empty ones),
   * every paragraph, and any nested-table content — plus the structural path
   * down to the table. Marks nothing for rows that aren't selected.
   */
  const commitRow = (row: CastTableRow, tablePath: ParagraphAncestor[]): void => {
    mutateAllIncluded(row, true);
    includedAncestors.add(row);
    for (const cell of row.children) includedAncestors.add(cell);
    for (const ancestor of tablePath) {
      ancestor.included = true;
      includedAncestors.add(ancestor);
    }
  };

  /**
   * Row-budgeted table expansion. Selects rows via `expandTableRows`, then
   * commits them in priority order against the global budget, stopping at the
   * first row that overflows. Returns false if the budget stopped it mid-table.
   */
  const expandTable = (triggerEntry: ParagraphEntry, table: CastTable): boolean => {
    const tableEntries = matchedIds
      .map(id => index.byId.get(id))
      .filter(
        (entry): entry is ParagraphEntry =>
          entry !== undefined && findContainingTable(entry) === table
      );

    const matchedRows = new Set<CastTableRow>();
    for (const entry of tableEntries) {
      const row = getRowOfEntry(entry, table);
      if (row) matchedRows.add(row);
    }

    // The structural path from the owning section down to (and including) the
    // table — shared by every row, so each row only adds its own subtree.
    const tableIndex = triggerEntry.ancestors.indexOf(table);
    const tablePath = triggerEntry.ancestors.slice(0, tableIndex + 1);

    const rows = expandTableRows(table, matchedRows, tableTokenLimit, estimateTokens);

    let tableFits = true;
    for (const row of rows) {
      const rowEntries = index.ordered.filter(entry => entry.ancestors.includes(row));
      const cost = estimateAdditionCost(
        rowEntries,
        includedAncestors,
        serializeOptions,
        estimateTokens
      );
      if (spent !== 0 && spent + cost > maxBudget) {
        tableFits = false;
        break;
      }
      commitRow(row, tablePath);
      spent += cost;
    }

    // Headings that scope the table, added if they fit.
    tryCommit(dedupe(tableEntries.flatMap(entry => expandHeadings(entry, index))));

    return tableFits;
  };

  for (const id of matchedIds) {
    if (spent >= maxBudget) break;

    const entry = index.byId.get(id);
    if (!entry) continue;

    const table = findContainingTable(entry);
    if (table) {
      if (processedTables.has(table)) continue;
      processedTables.add(table);
      if (!expandTable(entry, table)) break;
      continue;
    }

    const candidates = dedupe([
      ...expandNeighbors(entry, index, neighborCount),
      ...expandParentListItems(entry, index),
      ...expandHeadings(entry, index),
    ]);
    if (!tryCommit(candidates)) break;
  }

  return root;
}

function dedupe(entries: ParagraphEntry[]): ParagraphEntry[] {
  const seen = new Set<ParagraphEntry>();
  const result: ParagraphEntry[] = [];
  for (const entry of entries) {
    if (seen.has(entry)) continue;
    seen.add(entry);
    result.push(entry);
  }
  return result;
}

function estimateAdditionCost(
  additions: ParagraphEntry[],
  includedAncestors: Set<ParagraphAncestor>,
  serializeOptions: SerializeOptions,
  estimateTokens: EstimateTokens
): number {
  let cost = 0;
  const newWrappers = new Set<ParagraphAncestor>();

  for (const entry of additions) {
    cost += estimateParagraphCost(entry.paragraph, serializeOptions, estimateTokens);
    for (const ancestor of entry.ancestors) {
      if (includedAncestors.has(ancestor) || newWrappers.has(ancestor)) continue;
      newWrappers.add(ancestor);
      cost += estimateWrapperCost(ancestor, serializeOptions, estimateTokens);
    }
  }
  return cost;
}
